/**
 * Small voxel-model builder used by the procedural weapon and pet models. Coordinates are voxel
 * indices (x across, y up, z along the barrel with z = 0 at the muzzle end). Boxes are
 * half-open. A builder can route drawing into named sub-parts (magazines, slides, spinning
 * barrels...) that the view model animates separately; the main grid always holds the
 * complete model.
 */

// Model palette slots ----------------------------------------------------------------------
export const PRI = 1;
export const SEC = 2;
export const ACC = 3;
export const GLOW = 4;
export const DARK = 5;
export const LIGHT = 6;
export const METAL = 7;
export const PRI_D = 8;
export const SEC_L = 9;
export const SEC_D = 10;
export const ACC_L = 11;
export const ACC_D = 12;
export const METAL_L = 13;
export const METAL_D = 14;
export const DARK_L = 15;
export const LENS = 16;
export const LENS_HI = 17;
export const BRASS = 18;
export const RED = 19;
export const GLOW_HI = 20;
export const WHITE = 21;
export const RUBBER = 22;
/** Number of fixed slots; camo colors are interned after CAMO_BASE. */
export const SLOT_COUNT = 23;
export const CAMO_BASE = 32;
export const EMISSIVE_SLOTS = [GLOW, LENS_HI, RED, GLOW_HI];

export type WeaponPartName = 'mag' | 'slide' | 'pump' | 'bolt' | 'cyl' | 'spin' | 'break';

/** Edge shading: base slot -> [top-edge highlight, bottom-edge shadow]. */
const BEVEL: Record<number, [number, number]> = {
  [PRI]: [LIGHT, PRI_D],
  [SEC]: [SEC_L, SEC_D],
  [ACC]: [ACC_L, ACC_D],
  [METAL]: [METAL_L, METAL_D],
  [DARK]: [DARK_L, RUBBER],
};

export class ModelBuilder<P extends string = WeaponPartName> {
  readonly grid: Uint8Array;
  readonly parts = new Map<P, Uint8Array>();
  private cur: Uint8Array | null = null;
  /** Model center line along x (continuous coordinate). */
  readonly cx: number;

  constructor(
    readonly sx: number,
    readonly sy: number,
    readonly sz: number,
  ) {
    this.grid = new Uint8Array(sx * sy * sz);
    this.cx = sx / 2;
  }

  index(x: number, y: number, z: number): number {
    return x + z * this.sx + y * this.sx * this.sz;
  }

  inside(x: number, y: number, z: number): boolean {
    return x >= 0 && y >= 0 && z >= 0 && x < this.sx && y < this.sy && z < this.sz;
  }

  get(x: number, y: number, z: number): number {
    x = Math.floor(x);
    y = Math.floor(y);
    z = Math.floor(z);
    return this.inside(x, y, z) ? this.grid[this.index(x, y, z)] : 0;
  }

  /** Routes subsequent drawing into a named part (null = static body). */
  part(name: P | null): this {
    if (!name) {
      this.cur = null;
      return this;
    }
    let g = this.parts.get(name);
    if (!g) {
      g = new Uint8Array(this.grid.length);
      this.parts.set(name, g);
    }
    this.cur = g;
    return this;
  }

  set(x: number, y: number, z: number, v: number): this {
    x = Math.floor(x);
    y = Math.floor(y);
    z = Math.floor(z);
    if (!this.inside(x, y, z)) return this;
    const i = this.index(x, y, z);
    this.grid[i] = v;
    if (this.cur && v) this.cur[i] = v;
    else for (const p of this.parts.values()) p[i] = 0;
    return this;
  }

  /** Half-open box. */
  box(x0: number, y0: number, z0: number, x1: number, y1: number, z1: number, v: number): this {
    for (let y = Math.floor(y0); y < y1; y++) for (let z = Math.floor(z0); z < z1; z++) for (let x = Math.floor(x0); x < x1; x++) this.set(x, y, z, v);
    return this;
  }

  /** Box of width w centered on the model axis. */
  c(w: number, y0: number, y1: number, z0: number, z1: number, v: number): this {
    const x0 = Math.round(this.cx - w / 2);
    return this.box(x0, y0, z0, x0 + w, y1, z1, v);
  }

  carve(x0: number, y0: number, z0: number, x1: number, y1: number, z1: number): this {
    return this.box(x0, y0, z0, x1, y1, z1, 0);
  }

  /** Centered carve. */
  cc(w: number, y0: number, y1: number, z0: number, z1: number): this {
    return this.c(w, y0, y1, z0, z1, 0);
  }

  /** Recolors existing voxels in a box (optionally only voxels currently equal to `only`). */
  paint(x0: number, y0: number, z0: number, x1: number, y1: number, z1: number, v: number, only = -1): this {
    for (let y = Math.floor(y0); y < y1; y++)
      for (let z = Math.floor(z0); z < z1; z++)
        for (let x = Math.floor(x0); x < x1; x++) {
          const cur = this.get(x, y, z);
          if (cur && (only < 0 || cur === only)) this.recolor(x, y, z, v);
        }
    return this;
  }

  /** Changes a voxel color without moving it between parts. */
  recolor(x: number, y: number, z: number, v: number): this {
    if (!this.inside(x, y, z)) return this;
    const i = this.index(x, y, z);
    if (!this.grid[i]) return this;
    this.grid[i] = v;
    for (const p of this.parts.values()) if (p[i]) p[i] = v;
    return this;
  }

  /** Paints both side faces (x = xl and its mirror) over a y/z window. */
  sides(xl: number, y0: number, y1: number, z0: number, z1: number, v: number): this {
    const xr = this.sx - 1 - xl;
    this.paint(xl, y0, z0, xl + 1, y1, z1, v);
    return this.paint(xr, y0, z0, xr + 1, y1, z1, v);
  }

  /** Sets a voxel and its mirror across the model axis. */
  sym(x: number, y: number, z: number, v: number): this {
    this.set(x, y, z, v);
    return this.set(this.sx - 1 - x, y, z, v);
  }

  /** Mirrored box (x0..x1 and its reflection). */
  symBox(x0: number, y0: number, z0: number, x1: number, y1: number, z1: number, v: number): this {
    this.box(x0, y0, z0, x1, y1, z1, v);
    return this.box(this.sx - x1, y0, z0, this.sx - x0, y1, z1, v);
  }

  /**
   * Rows from yTop-1 down to yBot, each shifted along z by floor(rowsDown * slope):
   * positive slope leans back (grips), negative leans forward (curved mags).
   */
  slant(w: number, yTop: number, yBot: number, z0: number, len: number, slope: number, v: number): this {
    for (let y = yTop - 1; y >= yBot; y--) {
      const s = Math.floor((yTop - 1 - y) * slope);
      this.c(w, y, y + 1, z0 + s, z0 + s + len, v);
    }
    return this;
  }

  /** Cylinder along z centered at (cx, cy) (continuous coords); optional hollow radius. */
  tube(cx: number, cy: number, z0: number, z1: number, r: number, v: number, inner = -1): this {
    const R = Math.ceil(r) + 1;
    for (let y = Math.floor(cy - R); y <= cy + R; y++)
      for (let x = Math.floor(cx - R); x <= cx + R; x++) {
        const d = (x + 0.5 - cx) ** 2 + (y + 0.5 - cy) ** 2;
        if (d > r * r || d < inner * inner) continue;
        for (let z = Math.floor(z0); z < z1; z++) this.set(x, y, z, v);
      }
    return this;
  }

  /** Cylinder along x centered at (cy, cz). */
  tubeX(cy: number, cz: number, x0: number, x1: number, r: number, v: number, inner = -1): this {
    const R = Math.ceil(r) + 1;
    for (let y = Math.floor(cy - R); y <= cy + R; y++)
      for (let z = Math.floor(cz - R); z <= cz + R; z++) {
        const d = (y + 0.5 - cy) ** 2 + (z + 0.5 - cz) ** 2;
        if (d > r * r || d < inner * inner) continue;
        for (let x = Math.floor(x0); x < x1; x++) this.set(x, y, z, v);
      }
    return this;
  }

  /** Cylinder along y centered at (cx, cz). */
  tubeY(cx: number, cz: number, y0: number, y1: number, r: number, v: number, inner = -1): this {
    const R = Math.ceil(r) + 1;
    for (let z = Math.floor(cz - R); z <= cz + R; z++)
      for (let x = Math.floor(cx - R); x <= cx + R; x++) {
        const d = (x + 0.5 - cx) ** 2 + (z + 0.5 - cz) ** 2;
        if (d > r * r || d < inner * inner) continue;
        for (let y = Math.floor(y0); y < y1; y++) this.set(x, y, z, v);
      }
    return this;
  }

  /** Sphere centered at a continuous point. */
  ball(cx: number, cy: number, cz: number, r: number, v: number): this {
    const R = Math.ceil(r) + 1;
    for (let y = Math.floor(cy - R); y <= cy + R; y++)
      for (let z = Math.floor(cz - R); z <= cz + R; z++)
        for (let x = Math.floor(cx - R); x <= cx + R; x++) if ((x + 0.5 - cx) ** 2 + (y + 0.5 - cy) ** 2 + (z + 0.5 - cz) ** 2 <= r * r) this.set(x, y, z, v);
    return this;
  }

  /** Ribbed rail (alternating shades) of width w on top of height y. */
  rail(y: number, z0: number, z1: number, w = 2): this {
    for (let z = z0; z < z1; z++) this.c(w, y, y + 1, z, z + 1, z % 2 ? DARK : DARK_L);
    return this;
  }

  /** Front post + rear notch iron sights; the sight line runs at height y + 2. */
  irons(y: number, zFront: number, zRear: number, frontColor = DARK): this {
    this.c(2, y, y + 2, zFront, zFront + 1, frontColor);
    this.c(4, y, y + 1, zRear, zRear + 2, METAL_D);
    const x0 = Math.round(this.cx - 2);
    this.box(x0, y + 1, zRear, x0 + 1, y + 2, zRear + 2, DARK);
    return this.box(x0 + 3, y + 1, zRear, x0 + 4, y + 2, zRear + 2, DARK);
  }

  /** Red-dot style optic sitting on height y: 4x4 housing with a see-through window; returns the sight height. */
  reflex(y: number, z0: number, len: number, trim = ACC): number {
    this.c(4, y, y + 4, z0, z0 + len, DARK);
    this.cc(2, y + 1, y + 3, z0, z0 + len);
    this.c(4, y, y + 4, z0, z0 + 1, trim);
    this.cc(2, y + 1, y + 3, z0, z0 + 1);
    this.c(2, y - 1, y, z0 + 1, z0 + len - 1, METAL_D);
    return y + 2;
  }

  /** Scope along z centered at (cx, cy) with objective/eyepiece bells, lenses and rings down to ringBase. */
  scope(cy: number, z0: number, z1: number, r: number, ringBase: number, ringColor = ACC): this {
    const cx = this.cx;
    this.tube(cx, cy, z0 + 3, z1 - 2, r, DARK);
    this.tube(cx, cy, z0, z0 + 4, r + 0.9, DARK);
    this.tube(cx, cy, z1 - 3, z1, r + 0.55, DARK);
    // Objective lens (with a glint) and eyepiece glass.
    this.tube(cx, cy, z0, z0 + 1, r + 0.2, LENS);
    this.set(Math.floor(cx) - 1, Math.floor(cy), z0, LENS_HI);
    this.tube(cx, cy, z1 - 1, z1, r - 0.2, LENS);
    // Turrets.
    const tz = Math.floor((z0 + z1) / 2);
    this.c(2, Math.floor(cy + r), Math.floor(cy + r) + 2, tz - 1, tz + 1, DARK_L);
    this.box(Math.floor(cx + r), Math.floor(cy) - 1, tz - 1, Math.floor(cx + r) + 1, Math.floor(cy) + 1, tz + 1, DARK_L);
    // Rings.
    for (const rz of [z0 + 5, z1 - 5]) {
      this.tube(cx, cy, rz, rz + 1, r + 0.5, ringColor);
      this.c(2, ringBase, Math.floor(cy - r) + 1, rz, rz + 1, ringColor);
    }
    return this;
  }

  /**
   * Auto shading: top edges of large colored surfaces get their highlight shade, bottom edges
   * their shadow shade. Keeps silhouettes crisp at 2.5 cm resolution.
   */
  bevel(): this {
    const { sx, sy, sz, grid } = this;
    const out = grid.slice();
    for (let y = 0; y < sy; y++)
      for (let z = 0; z < sz; z++)
        for (let x = 0; x < sx; x++) {
          const i = this.index(x, y, z);
          const v = grid[i];
          const pair = BEVEL[v];
          if (!pair) continue;
          const sideOpen = this.get(x - 1, y, z) === 0 || this.get(x + 1, y, z) === 0;
          const endOpen = this.get(x, y, z - 1) === 0 || this.get(x, y, z + 1) === 0;
          if (this.get(x, y + 1, z) === 0 && (sideOpen || endOpen)) out[i] = pair[0];
          else if (this.get(x, y - 1, z) === 0 && sideOpen) out[i] = pair[1];
        }
    grid.set(out);
    for (const p of this.parts.values()) for (let i = 0; i < p.length; i++) if (p[i]) p[i] = out[i];
    return this;
  }
}
